import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store.ts";
import {
  LocationCreate,
  CompanyCreate,
  DriverCreate,
  VehicleCreate,
  ProductCreate,
} from "../../api";

export type AddNewFieldType =
  | "address"
  | "company"
  | "driver"
  | "vehicle"
  | "product";

export type AddNewSimpleFieldType = "product";

export interface AddNewTarget<T extends AddNewFieldType = AddNewFieldType> {
  id?: number;
  field: string;
  initialValues: T extends "address"
    ? Partial<LocationCreate>
    : T extends "company"
      ? Partial<CompanyCreate>
      : T extends "driver"
        ? Partial<DriverCreate>
        : T extends "vehicle"
          ? Partial<VehicleCreate>
          : T extends "product"
            ? Partial<ProductCreate>
            : Record<string, unknown>;
  type: T;
}

export type AddNewCallbackData = Omit<
  AddNewTarget,
  "type" | "field" | "initialValues"
>;

export interface AddNewState {
  target?: AddNewTarget;
  lastAdded?: { field: string; value: string | number };
}

const initialState: AddNewState = {};

export const addNewSlice = createSlice({
  name: "addNew",
  initialState,
  reducers: {
    openAddNewDialog: (
      state,
      {
        payload,
      }: PayloadAction<{
        target: AddNewTarget;
      }>,
    ) => {
      state.target = payload.target;
    },
    closeAddNewDialog: (state) => {
      state.target = undefined;
    },
    setLastAdded: (
      state,
      { payload }: PayloadAction<{ value: string | number }>,
    ) => {
      if (state.target?.field) {
        state.lastAdded = {
          field: state.target.field,
          value: payload.value,
        };
      }
    },
    clearLastAdded: (state) => {
      state.lastAdded = undefined;
    },
  },
});

export type AddNewCallback<TRow extends Record<string, unknown>> = (options: {
  dispatch: AppDispatch;
  row: TRow;
  data: AddNewCallbackData;
  initialValueString: string;
}) => void;

export const makeAddNewCallback = <
  TRow extends Record<string, unknown> = Record<string, unknown>,
>({
  field,
  type,
  makeInitialValues,
}: {
  field: string;
  type: AddNewFieldType;
  makeInitialValues?: (options: {
    initialValueString: string;
    row: TRow;
  }) => Record<string, unknown>;
}): AddNewCallback<TRow> => {
  return function addNewCallBack({ dispatch, data, initialValueString, row }) {
    dispatch(
      addNewSlice.actions.openAddNewDialog({
        target: {
          ...data,
          field,
          type,
          initialValues: makeInitialValues
            ? makeInitialValues({ initialValueString, row })
            : {},
        },
      }),
    );
  };
};
